import { z } from 'zod';
import { PopulatedUserSchema } from '../user/user.schema';
import { PopulatedProcessSchema } from '../process/process.schema';
import { PopulatedSystemSchema } from '../system/system.schema';
import { PopulatedOrgFunctionSchema } from '../org-function/org-function.schema';
import { PopulatedExternalContactSchema } from '../external-contact/external-contact.schema';
import { PopulatedProcessStepRecordingSchema } from '../process-step-recording/process-step-recording.schema';
import { ProcessEventNodeSchema } from '../process-node/process-node.schema';
import { EntityOrSuggestionRefSchema } from '../entity-suggestion';
import { StepInputSchema } from '../../shared/step-input.schema';
import { StepActionTypeSchema } from '../../shared/step-action-type.schema';
import { AssigneeModelSchema } from '../../shared/assignee-model.schema';
/**
 * Process Step Assignment Status Enum
 */ export const ProcessStepAssignmentStatusSchema = z.enum([
    'pending',
    'accepted',
    'rejected-reassigned',
    'rejected-unclear',
    'pending-unclear',
    'unclear-reassigned'
]);
/**
 * Process Step Assignment Schema
 * Represents a pending task assigned to a user, system, orgFunction, or external contact
 */ export const ProcessStepAssignmentSchema = z.object({
    _id: z.string(),
    process: z.union([
        z.string(),
        PopulatedProcessSchema
    ]),
    /**
   * The assignment's source node. Start-anchored rows carry the start event
   * node's id since issue 11 (ADR 0003 P5) and populate to the event display
   * arm. Rows on migration M2's orphan-repair list (no start node to rewrite
   * onto) still carry the legacy `'START'` marker — readers keep that arm
   * one release past P6.
   */ sourceProcessStep: z.union([
        z.string(),
        z.literal('START'),
        PopulatedProcessStepRecordingSchema,
        ProcessEventNodeSchema
    ]),
    stableStepId: z.string(),
    inputs: z.array(StepInputSchema).optional(),
    ruleIds: z.array(z.string()).optional(),
    assignedBy: z.union([
        z.string(),
        PopulatedUserSchema
    ]),
    assignedToModel: AssigneeModelSchema,
    /**
   * Assignee. A suggestion-placeholder row ships a bare
   * `{ type: 'suggestion', id }` ref (a published node assigned to a
   * still-pending proposed OrgFunction/System with no real entity yet — the
   * frontend resolves its proposed name from `entitySuggestions`). An entity
   * row ships the populated entity (the backend resolves it on read). The
   * discriminated ref arm is listed first so a `{ type, id }` ref never
   * mis-parses as a populated entity.
   */ assignedTo: z.union([
        EntityOrSuggestionRefSchema,
        PopulatedUserSchema,
        PopulatedSystemSchema,
        PopulatedOrgFunctionSchema,
        PopulatedExternalContactSchema
    ]).optional(),
    status: ProcessStepAssignmentStatusSchema,
    /**
   * Who enters the data for an `ExternalContact` assignee. The contact does the
   * work but is not a system user, so somebody records on their behalf. Only
   * meaningful for `assignedToModel: 'ExternalContact'`.
   *
   * Unset means "whoever owns the process right now" — every reader derives the
   * recorder from the live owner rather than from a stamped copy, so an
   * ownership transfer moves these rows with it. Never populated: no surface
   * needs the delegate's profile, only whether one exists.
   */ recordingDelegate: z.string().optional(),
    processStepRecording: z.string().optional(),
    previousStepAssignment: z.string().optional(),
    completedAt: z.number().optional(),
    deletedAt: z.string().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional()
});
/**
 * In Progress Recording Schema
 * Represents an incomplete ProcessStepRecording for the user
 */ export const InProgressRecordingSchema = z.object({
    _id: z.string(),
    process: z.union([
        z.string(),
        PopulatedProcessSchema
    ]),
    previousStep: z.union([
        z.string(),
        PopulatedProcessStepRecordingSchema
    ]),
    recordedBy: z.union([
        z.string(),
        PopulatedUserSchema
    ]),
    events: z.array(z.object({
        eventType: z.string(),
        name: z.string(),
        description: z.string()
    })).optional(),
    inputs: z.array(z.string()).optional(),
    actionType: StepActionTypeSchema,
    task: z.object({
        taskDescription: z.string(),
        output: z.string()
    }).optional(),
    dueDate: z.number().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional()
});

//# sourceMappingURL=process-step-assignment.schema.js.map